import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import { selectAccountSlug } from '@/redux/session/selectors';

/**
 * ListEmptyState - Empty state component shown when no email integrations exist
 *
 * Displays:
 * - Email illustration from Figma design
 * - Title and description
 * - Call-to-action button to add first email
 *
 * Design matches Figma spec:
 * - 220x190px illustration
 * - 32px gap between content sections
 * - 16px gap between illustration and text
 * - 12px gap between title and description
 * - Centered layout with 384px max width
 *
 * @returns {JSX.Element} The empty state component
 */
export const ListEmptyState = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center p-6">
      {/* Empty State Container - 384px width as per Figma */}
      <div className="flex w-96 flex-col items-center gap-8">
        {/* Content: Illustration + Text */}
        <div className="flex flex-col items-center gap-4">
          {/* Illustration - 220x190px */}
          <img
            src="/assets/email-empty-state.svg"
            alt="Email illustration"
            width={220}
            height={190}
          />

          {/* Text Content */}
          <div className="flex flex-col gap-3">
            {/* Title */}
            <h2 className="text-center text-2xl leading-none font-semibold tracking-[-0.025em] text-slate-950">
              {t('email.add_email')}
            </h2>

            {/* Description */}
            <p className="text-center text-sm leading-5 text-slate-500">
              {t('email.empty_state_description')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex w-full items-center gap-3">
          <Link to={`/${slug}/environments/${deskId}/integrations/email/draft`} className="w-full">
            <Button size="md" className="w-full">
              <PlusIcon className="h-4 w-4" />
              {t('email.add_email')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
